import { ReactNode } from 'react';

import {
  Box,
  Center,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Link,
  Separator,
  Text,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import { ArrowBack, CalendarMonth, Check, Directions, DirectionsWalk, Edit, Info } from '@m/icons';

import { Button } from '@m/components-ui/button';
import { CloseButton } from '@m/components-ui/close-button';
import { CostRow, SectionHeader } from '@m/components-ui/mobile-rows';
import { RadioCardItem, RadioCardRoot } from '@m/components-ui/radio-card';
import { Tag } from '@m/components-ui/tag';
import { Tooltip } from '@m/components-ui/tooltip';

import { HeroImageCarousel, heroCarouselUrls } from './hero-image-carousel';

export interface PlanOption {
  id: string;
  label: string;
  priceLabel: string;
}

export interface HoursRow {
  hours: string;
  label: string;
}

export interface HowItWorksStep {
  description: string;
  image: ReactNode;
}

export interface Amenity {
  icon: ReactNode;
  label: string;
}

export interface LocationDetailsPanelProps {
  acceptedPayments?: ReactNode;
  /** Additional sections rendered at the top of the left content area (before amenities) */
  additionalSections?: ReactNode;
  amenities?: Amenity[];
  /** Back-navigation link shown above the location name */
  backNavHref?: string;
  backNavLabel?: string;
  /** Custom booking widget — when provided, replaces the default booking card content */
  bookingWidget?: React.ReactElement | null;
  bookNowLabel?: string;
  directionsHref?: string;
  directionsLabel?: string;
  entranceAddress?: string;
  freeCancellation?: boolean;
  hours?: HoursRow[];
  howItWorks?: HowItWorksStep[];
  imageAlt?: string;
  imageUrl?: string;
  /** Ordered photo URLs. Only the first four are shown. */
  imageUrls?: readonly string[];
  locationName: string;
  onBookNowClick?: () => void;
  onClose?: () => void;
  onDirectionsClick?: () => void;
  onEditDate?: () => void;
  onPlanChange?: (planId: string) => void;
  plans?: PlanOption[];
  priceFormatted?: string;
  priceLabel?: string;
  selectedPlanId?: string;
  /** Whether to show the hero image. Defaults to true. */
  showImage?: boolean;
  startingDate?: string;
  supportPhone?: string;
  walkDistanceFormatted?: string;
  walkTimeMinutes?: number;
}

export function LocationDetailsPanel({
  acceptedPayments,
  additionalSections,
  amenities,
  backNavHref,
  backNavLabel = 'Search for parking',
  bookNowLabel = 'Book now',
  bookingWidget,
  directionsHref,
  directionsLabel = 'Directions',
  entranceAddress,
  freeCancellation = true,
  hours,
  howItWorks,
  imageAlt = 'Parking location',
  imageUrls,
  imageUrl,
  locationName,
  onBookNowClick,
  onClose,
  onDirectionsClick,
  onEditDate,
  onPlanChange,
  plans,
  priceFormatted,
  priceLabel = 'Per month',
  selectedPlanId,
  showImage = true,
  startingDate,
  supportPhone,
  walkDistanceFormatted,
  walkTimeMinutes,
}: LocationDetailsPanelProps) {
  const showWalkInfo = walkTimeMinutes !== undefined && walkDistanceFormatted !== undefined;
  const walkInfo = showWalkInfo ? `${walkTimeMinutes} min (${walkDistanceFormatted})` : '';
  const showBooking =
    bookingWidget != null ||
    onBookNowClick !== undefined ||
    priceFormatted !== undefined ||
    startingDate !== undefined ||
    (plans !== undefined && plans.length > 0);
  const showDirections = onDirectionsClick !== undefined || directionsHref !== undefined;

  // Shared sub-components used in both layout branches

  const heroImage = showImage && (
    <Box
      position="relative"
      h={{ base: '211px', md: '320px' }}
      overflow="hidden"
      flexShrink={0}
      borderRadius={{ base: '0', md: '16px' }}
    >
      <HeroImageCarousel alt={imageAlt} urls={heroCarouselUrls(imageUrls, imageUrl)} />
      {backNavHref && (
        <Link
          href={backNavHref}
          aria-label={backNavLabel}
          position="absolute"
          top="4"
          left="4"
          zIndex={1}
          hideFrom="md"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="36px"
          h="36px"
          borderRadius="6px"
          bg="fill.button.navigation.enabled"
          color="fill.icon.interactive"
        >
          <ArrowBack />
        </Link>
      )}
    </Box>
  );

  const backNav = backNavHref && (
    <Box
      hideBelow={showImage ? 'md' : undefined}
      pb={{ base: '4', md: '6' }}
      pt={{ base: '8', md: '2' }}
      px={{ base: '4', md: '0' }}
    >
      <Link
        alignItems="center"
        color="text.secondary"
        display="inline-flex"
        gap="2"
        href={backNavHref}
        textDecoration="none"
        _hover={{ color: 'text.primary' }}
      >
        <Box as="span" fontSize="24px" lineHeight="0">
          <ArrowBack />
        </Box>
        <Text color="inherit" variant="MainBodyMedium">
          {backNavLabel}
        </Text>
      </Link>
    </Box>
  );

  const locationHeader = (
    <HStack
      align="flex-start"
      justify="space-between"
      pt={showImage ? '6' : '0'}
      px={{ base: '4', md: '0' }}
      pb={{ base: '6', md: '8' }}
    >
      <VStack align="stretch" gap="1" flex={1} minW={0}>
        <Text variant="Header.H3" color="text.primary">
          {locationName}
        </Text>
        <VStack align="stretch" gap={{ base: '4', md: '6' }}>
          {entranceAddress && (
            <Text variant="MainBodyRegular" color="text.secondary">
              Entrance: {entranceAddress}
            </Text>
          )}
          <HStack gap="4" align="center" flexWrap="wrap">
            {showWalkInfo && (
              <Tag size="sm" uncontained startElement={<DirectionsWalk />}>
                {walkInfo}
              </Tag>
            )}
            {freeCancellation && (
              <HStack gap="1">
                <Tag size="sm" variant="success" startElement={<Check />}>
                  Free cancellation &amp; changes
                </Tag>
                <Tooltip
                  content="Reservations can be canceled until the start time. Once a reservation starts, it can be extended."
                  contentProps={{ maxW: '240px' }}
                >
                  <Box color="text.secondary" cursor="default" aria-label="Free cancellation info">
                    <Info />
                  </Box>
                </Tooltip>
              </HStack>
            )}
          </HStack>
        </VStack>
      </VStack>

      {showDirections &&
        (onDirectionsClick ? (
          <>
            <IconButton
              variant="secondary"
              size="sm"
              flexShrink={0}
              onClick={onDirectionsClick}
              aria-label={directionsLabel}
              hideFrom="md"
            >
              <Directions />
            </IconButton>
            <Button
              variant="secondary"
              size="md"
              flexShrink={0}
              onClick={onDirectionsClick}
              hideBelow="md"
            >
              <Directions />
              {directionsLabel}
            </Button>
          </>
        ) : (
          <>
            <IconButton
              asChild
              variant="secondary"
              size="sm"
              flexShrink={0}
              aria-label={directionsLabel}
              hideFrom="md"
            >
              <a href={directionsHref} target="_blank" rel="noopener noreferrer">
                <Directions />
              </a>
            </IconButton>
            <Button asChild variant="secondary" size="md" flexShrink={0} hideBelow="md">
              <a href={directionsHref} target="_blank" rel="noopener noreferrer">
                <Directions />
                {directionsLabel}
              </a>
            </Button>
          </>
        ))}
    </HStack>
  );

  const bookingContent = bookingWidget ?? (
    <VStack
      align="stretch"
      gap="2"
      px="6"
      pt={{ base: '0', md: '2' }}
      pb={{ base: '0', md: '6' }}
      border={{ md: '1px solid' }}
      borderColor={{ md: 'stroke.divider.emphasized' }}
      borderRadius={{ md: '2xl' }}
    >
      <Box hideBelow="md">
        <SectionHeader title="Reserve parking">Secure your spot in seconds.</SectionHeader>
      </Box>

      <Separator />

      {plans && plans.length > 0 && (
        <RadioCardRoot
          value={selectedPlanId}
          onValueChange={(details) => {
            if (details.value) onPlanChange?.(details.value);
          }}
          pt="2"
        >
          {plans.map((plan) => (
            <RadioCardItem
              key={plan.id}
              value={plan.id}
              label={plan.label}
              description={plan.priceLabel}
              width="full"
            />
          ))}
        </RadioCardRoot>
      )}

      {/* Pricing summary */}
      {(priceFormatted !== undefined || startingDate !== undefined) && (
        <HStack justify="space-between" align="flex-end" py="2">
          {startingDate !== undefined && (
            <VStack align="flex-start" gap="0">
              <Text variant="SmallBodyMedium">Starting date</Text>
              <HStack gap="4" align="center">
                <Text variant="MainBodyDemiBold" color="text.primary">
                  {startingDate}
                </Text>
                {onEditDate && (
                  <IconButton variant="tertiary" size="xs" onClick={onEditDate}>
                    <Edit />
                  </IconButton>
                )}
              </HStack>
            </VStack>
          )}
          {priceFormatted !== undefined && (
            <VStack align="flex-end" gap="1">
              <Text variant="Subheader" color="text.primary">
                {priceFormatted}
              </Text>
              <Text variant="SmallBodyRegular" color="text.secondary">
                {priceLabel}
              </Text>
            </VStack>
          )}
        </HStack>
      )}

      {onBookNowClick && (
        <Button variant="brand" size="md" w="full" onClick={onBookNowClick}>
          <CalendarMonth />
          {bookNowLabel}
        </Button>
      )}
    </VStack>
  );

  const informationalSections = (
    <>
      {additionalSections}
      {hours && hours.length > 0 && (
        <VStack align="stretch" gap="0" px={{ base: '4', md: '0' }}>
          <Separator />
          <SectionHeader title="Hours" />
          <VStack align="stretch" gap="0" pt="2" pb="6">
            {hours.map((row, index) => (
              <CostRow key={row.label ?? index} title={row.label} amount={row.hours} />
            ))}
          </VStack>
        </VStack>
      )}

      {amenities && amenities.length > 0 && (
        <VStack align="stretch" gap="0" px={{ base: '4', md: '0' }}>
          <Separator />
          <SectionHeader title="Site amenities" />
          <Wrap gap="4" pt="2" pb="6">
            {amenities.map((amenity) => (
              <Tag key={amenity.label} size="sm" startElement={amenity.icon}>
                {amenity.label}
              </Tag>
            ))}
          </Wrap>
        </VStack>
      )}

      {acceptedPayments && (
        <VStack align="stretch" gap="0" px={{ base: '4', md: '0' }}>
          <Separator />
          <SectionHeader title="Accepted payments" />
          <Box pt="2" pb="6">
            {acceptedPayments}
          </Box>
        </VStack>
      )}

      {howItWorks && howItWorks.length > 0 && (
        <VStack align="stretch" gap="0" px={{ base: '4', md: '0' }} pb={{ base: '12', md: '16' }}>
          <Separator />
          <SectionHeader title="How it works" />
          <VStack align="stretch" gap="4" pt="2" pb="6">
            {howItWorks.map((step, i) => (
              <HStack key={i} gap="6" align="center">
                <Center
                  flexShrink={0}
                  w="64px"
                  h="64px"
                  bg="brand.000"
                  borderRadius="md"
                  p="1"
                  aria-hidden="true"
                >
                  {step.image}
                </Center>
                <Text variant="SmallBodyRegular" color="text.primary" flex={1}>
                  {step.description}
                </Text>
              </HStack>
            ))}
          </VStack>

          {supportPhone && (
            <Box pt="2" pb="4">
              <Text variant="SmallBodyRegular" color="text.primary">
                <Text as="span">Need help?</Text> Call our support team:{' '}
                <a href={`tel:${supportPhone}`} style={{ textDecoration: 'underline' }}>
                  {supportPhone}.
                </a>
              </Text>
            </Box>
          )}
        </VStack>
      )}
    </>
  );

  return (
    <Box bg={{ base: 'fill.background.default', md: 'transparent' }} w="full" position="relative">
      {onClose && (
        <CloseButton
          position="absolute"
          top="4"
          right="4"
          zIndex="1"
          size="xs"
          bg="fill.button.primary.enabled"
          color="text.white"
          borderRadius="full"
          onClick={onClose}
          hideFrom="md"
        />
      )}

      {showBooking ? (
        /* Desktop keeps the booking card in a sticky right column. Mobile places
           it under the location header and above drive-up rates. Rendered once
           so booking state is not mounted twice. The card is an inner sticky
           element: the grid item spans the full left column, which is what
           makes position:sticky release at the end of the details. */
        <Grid
          templateColumns={{ base: '1fr', md: 'minmax(0, 1fr) 375px' }}
          columnGap={{ md: '114px' }}
          templateAreas={{
            base: `"intro" "booking" "details"`,
            md: `"intro booking" "details booking"`,
          }}
        >
          <GridItem area="intro" minW={0}>
            {backNav}
            {heroImage}
            {locationHeader}
          </GridItem>
          {/* Booking precedes the detail sections in the DOM so mobile reading
              and tab order match the visual order. Desktop placement still comes
              from the grid areas. */}
          <GridItem area="booking" minW={0}>
            <Box position={{ md: 'sticky' }} top={{ md: '28' }}>
              {bookingContent}
            </Box>
          </GridItem>
          <GridItem area="details" minW={0}>
            {informationalSections}
          </GridItem>
        </Grid>
      ) : (
        /* Single-column Grid layout when no booking widget */
        <Grid
          templateAreas={
            showImage
              ? {
                  base: `"nav" "image" "header" "sections"`,
                  md: `"nav" "image" "header" "sections"`,
                }
              : { base: `"nav" "header" "sections"`, md: `"nav" "header" "sections"` }
          }
          templateColumns="1fr"
        >
          {showImage && <GridItem area="image">{heroImage}</GridItem>}
          {backNavHref && <GridItem area="nav">{backNav}</GridItem>}
          <GridItem area="header">{locationHeader}</GridItem>
          <GridItem area="sections">{informationalSections}</GridItem>
        </Grid>
      )}
    </Box>
  );
}
